import { useState, useEffect } from "react";
import { wabaApi } from "../../lib/wabaApi";

// Mesmo app do WhatsApp; a configuração é outra porque a variação "Cadastro
// incorporado do WhatsApp" não aceita permissões de Instagram.
const META_APP_ID = "843063592187222";
const IG_CONFIG_ID = "2019580048696525";

interface Props {
  connectionId: string;
  onSuccess: () => void;
}

/**
 * Texto legível do erro. O `detail` da API pode ser string OU o objeto de erro
 * da Meta ({error, meta_code, meta_message, ...}) — jogar o objeto no state faz
 * o React estourar ao renderizá-lo como filho, e a mensagem real se perde.
 */
function errorText(err: any): string {
  const d = err?.payload?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    return d.meta_message || d.error || JSON.stringify(d);
  }
  return "Erro ao vincular a conta do Instagram.";
}

export function InstagramSignup({ connectionId, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega o SDK da Meta (idempotente — o EmbeddedSignup usa o mesmo script).
  useEffect(() => {
    if (document.getElementById("fb-jssdk")) return;
    window.fbAsyncInit = () => {
      window.FB.init({ appId: META_APP_ID, autoLogAppEvents: true, xfbml: true, version: "v25.0" });
    };
    const script = document.createElement("script");
    script.id = "fb-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true; script.defer = true;
    document.body.appendChild(script);
  }, []);

  async function doLink(code: string) {
    try {
      await wabaApi.post(`/connections/${connectionId}/link-instagram`, { code });
      onSuccess();
    } catch (err: any) {
      console.error("Erro ao vincular Instagram:", err);
      setError(errorText(err));
    } finally { setLoading(false); }
  }

  function handleSignup() {
    if (!window.FB) { setError("SDK da Meta ainda não carregou. Aguarde e tente novamente."); return; }
    setError(null); setLoading(true);

    // Callback SÍNCRONO: o SDK da Meta valida o tipo e recusa async function
    // ("Expression is of type asyncfunction, not function"). O trabalho async
    // sai em doLink — mesmo padrão do EmbeddedSignup.
    //
    // Sem postMessage aqui: diferente do Embedded Signup do WhatsApp, o Login
    // for Business devolve só o code. Página e conta de IG saem do /me/accounts
    // no backend.
    window.FB.login((response: any) => {
      const code = response?.authResponse?.code;
      if (!code) {
        setError("Cadastro cancelado ou código não retornado pela Meta.");
        setLoading(false); return;
      }
      doLink(code);
    }, {
      config_id: IG_CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        onClick={handleSignup}
        disabled={loading}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 18px",
          background: loading ? "var(--surface-input)" : "#C13584",
          color: loading ? "var(--ink-muted)" : "#fff", border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
        {loading ? "Conectando..." : "Conectar Instagram"}
      </button>
      {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}
